import { marketsArray, getTokenIcon, getTokenColor } from '../data/marketInfo';
/**
 * Extracts the symbol from a full Move object ID coinType
 * @param coinType - Full Sui object type (e.g., "fe3afec26c59e874f3c1d60b8203cb3852d2bb2aa415df9548b8d688e6683f93::alpha::ALPHA")
 * @returns The extracted symbol (e.g., "ALPHA")
 */
export const extractSymbolFromCoinType = (coinType) => {
    var _a;
    return ((_a = coinType.split('::').pop()) === null || _a === void 0 ? void 0 : _a.toUpperCase()) || 'UNKNOWN';
};
/**
 * Creates a TokenConfig object from a coinType by looking up market data
 * @param coinType - Full Sui object type or symbol
 * @returns TokenConfig object with symbol, decimals, color, displayName, and iconUrl
 */
export const getTokenConfig = (coinType) => {
    const tokenIcons = getTokenIcon();
    // Extract symbol from full coinType if it's a Move object ID
    const symbol = coinType.includes('::')
        ? extractSymbolFromCoinType(coinType)
        : coinType.toUpperCase();
    // Find market by symbol with priority:
    // 1. Exact symbol match (case insensitive)
    // 2. Match by coinType's last segment (e.g., "BTC" matches "::btc::BTC")
    let market = marketsArray.find((m) => m.symbol.toLowerCase() === symbol.toLowerCase());
    // If no exact match, try matching by coinType's last segment
    if (!market) {
        market = marketsArray.find((m) => {
            var _a;
            const marketCoinTypeSymbol = (_a = m.coinType.split('::').pop()) === null || _a === void 0 ? void 0 : _a.toUpperCase();
            return marketCoinTypeSymbol === symbol.toUpperCase();
        });
    }
    if (market) {
        // Get the actual icon URL
        const iconUrl = market.coinType
            ? tokenIcons[market.coinType]
            : market.icon;
        return {
            symbol: market.symbol,
            decimals: market.expo || 9,
            color: 'bg-gray-400',
            displayName: market.symbol,
            iconUrl: iconUrl || '',
        };
    }
    // Fallback for unknown tokens
    return {
        symbol: symbol,
        decimals: 6,
        color: 'bg-gray-400',
        displayName: symbol,
        iconUrl: '',
    };
};
/**
 * Gets token icon URL from market data
 * @param coinType - Full Sui object type or symbol
 * @returns Icon URL string
 */
export const getTokenIconUrl = (coinType) => {
    const tokenIcons = getTokenIcon();
    const symbol = coinType.includes('::')
        ? extractSymbolFromCoinType(coinType)
        : coinType.toUpperCase();
    const market = marketsArray.find((m) => m.symbol.toLowerCase() === symbol.toLowerCase() ||
        m.symbol.toLowerCase().includes(symbol.toLowerCase()));
    if (market) {
        return market.coinType
            ? tokenIcons[market.coinType] || market.icon || ''
            : market.icon || '';
    }
    return '';
};
/**
 * Gets token icon URL from market data using marketId
 * @param position - Position object with markets array
 * @returns Icon URL string
 */
export const getTokenIconUrlByMarketId = (position) => {
    var _a, _b;
    const tokenIcons = getTokenIcon();
    // Get market_id from position
    const marketId = (_b = (_a = position.markets) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.market_id;
    const market = marketId ? marketsArray.find((m) => m.id === marketId.toString()) : undefined;
    if (market) {
        return market.coinType
            ? tokenIcons[market.coinType] || market.icon || ''
            : market.icon || '';
    }
    return '';
};
/**
 * Gets token config by symbol only (for net worth display)
 * @param symbol - Token symbol (e.g., "USDT", "SUI")
 * @returns TokenConfig object
 */
export const getTokenConfigBySymbol = (symbol) => {
    const tokenIcons = getTokenIcon();
    // Find market by symbol with priority:
    // 1. Exact symbol match (case insensitive)
    // 2. Match by coinType's last segment (e.g., "BTC" matches "::btc::BTC")
    let market = marketsArray.find((m) => m.symbol.toLowerCase() === symbol.toLowerCase());
    // If no exact match, try matching by coinType's last segment
    if (!market) {
        market = marketsArray.find((m) => {
            var _a;
            const marketCoinTypeSymbol = (_a = m.coinType.split('::').pop()) === null || _a === void 0 ? void 0 : _a.toUpperCase();
            return marketCoinTypeSymbol === symbol.toUpperCase();
        });
    }
    if (market) {
        // Get the actual icon URL
        const iconUrl = market.coinType
            ? tokenIcons[market.coinType]
            : market.icon;
        return {
            symbol: market.symbol,
            decimals: market.expo || 9,
            color: 'bg-gray-400',
            displayName: market.symbol,
            iconUrl: iconUrl || '',
        };
    }
    // Fallback for unknown tokens
    return {
        symbol: symbol,
        decimals: 6,
        color: 'bg-gray-400',
        displayName: symbol,
        iconUrl: '',
    };
};
/**
 * Converts raw amount to actual token amount using token decimals
 * @param rawAmount - Raw amount string from backend
 * @param coinType - Token coinType for decimal lookup
 * @returns Converted amount as number
 */
export const convertRawAmount = (rawAmount, coinType) => {
    const config = getTokenConfig(coinType);
    return parseFloat(rawAmount) / Math.pow(10, config.decimals);
};
/**
 * Converts closing net worth tokens from database format to actual amounts
 * This is specific to the closing net worth tokens in the account history records
 * and preserves the original sign of the amount (positive or negative)
 * @param tokens - Raw closing net worth tokens from backend
 * @returns Converted amounts as numbers
 */
export const convertClosingNetWorthTokens = (tokens) => {
    if (!tokens)
        return {};
    const result = {};
    Object.entries(tokens).forEach(([symbol, amount]) => {
        const config = getTokenConfigBySymbol(symbol);
        const rawAmount = parseFloat(amount);
        const integerAmount = Math.floor(Math.abs(rawAmount));
        const convertedAmount = integerAmount / Math.pow(10, config.decimals);
        result[symbol] = rawAmount >= 0 ? convertedAmount : -convertedAmount;
    });
    return result;
};
/**
 * Get color for a token by symbol
 * @param tokenSymbol - The token symbol (e.g., 'SUI', 'USDC')
 * @returns Hex color code
 */
export const getColorBySymbol = (tokenSymbol) => {
    return getTokenColor(tokenSymbol);
};
/**
 * Get color for a token by market ID
 * @param marketId - The market ID (e.g., '1', '2')
 * @returns Hex color code
 */
export const getColorByMarketId = (marketId) => {
    return getTokenColor('', marketId);
};
/**
 * Get dynamic decimal places for value display
 * @param value
 * @param expo
 * @returns Number of decimal places to display
 */
export const getDecimalPlaces = (value, expo) => {
    if (value === 0)
        return 2;
    if (value >= 0.01)
        return 2;
    // For values < 0.01, check if they'd show as 0.0000
    const withFourDecimals = parseFloat(value.toFixed(4));
    if (withFourDecimals === 0) {
        return expo; // Show all token decimals
    }
    return 4;
};
/**
 * Convert raw token amount (string) to decimal number
 * @param rawAmount - Raw amount as string (e.g., "1000000000")
 * @param decimals - Token decimals (e.g., 9 for SUI)
 * @returns Converted number (e.g., 1.0)
 */
export const fromRawAmount = (rawAmount, decimals) => {
    if (!rawAmount)
        return 0;
    return parseFloat(rawAmount.toString()) / Math.pow(10, decimals);
};
/**
 * Convert decimal number to raw token amount string
 * @param amount - Decimal amount (e.g., 1.5)
 * @param decimals - Token decimals (e.g., 9)
 * @returns Raw amount string (e.g., "1500000000")
 */
export const toRawAmount = (amount, decimals) => {
    return Math.floor(amount * Math.pow(10, decimals)).toString();
};
